import {
  Button,
  Divider,
  Flex,
  TextField,
  SwitchField,
  PhoneNumberField,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import { useCareProvidersPostMutation } from "./api/useCareProvidersMutation";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserQuery } from "../common/useUserQuery";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const defaultValues = {
  providerName: "",
  providerSpecialty: "",
  providerSubSpecialty: "",
  //providerEmail: '',
  dialCode: "",
  providerPhone: "",
  providerHospitalAffiliation: "",
  providerHospitalName: "",
  providerHospitalAddress1: "",
  providerHospitalAddress2: "",
  providerHospitalCity: "",
  providerHospitalState: "",
  providerHospitalZip: "",
  providerImageUrl: "",
  isPrimaryProvider: false,
};

const phonePattern = /^\d*$/;

const zodRequest = z.object({
  providerName: z
    .string({ message: "Name is Required" })
    .min(1, { message: "Name is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  providerSpecialty: z
    .string({ message: "Specialty is Required" })
    .min(1, { message: "Specialty is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  providerSubSpecialty: z.optional(
    z.string().max(100, { message: "Must be 100 or fewer characters long" })
  ),
  providerEmail: z
    .union([
      z
        .string()
        .length(0, "Must be a valid email address (e.g., example@example.com)"),
      z.string().email({
        message: "Must be a valid email address (e.g., example@example.com)",
      }),
    ])
    .optional()
    .transform((e) => (e === "" ? null : e))
    .nullish(),
  dialCode: z.any(),
  providerPhone: z.union([
    z.string().length(0, "Phone must be only numbers. (e.g., 23456789012)"),
    z.string().regex(phonePattern, {
      message: "Phone must be only numbers. (e.g., 23456789012)",
    }),
  ]),
  providerHospitalAffiliation: z.optional(
    z.string().max(100, { message: "Must be 100 or fewer characters long" })
  ),
  providerHospitalName: z.optional(
    z.string().max(100, { message: "Must be 100 or fewer characters long" })
  ),
  providerHospitalAddress1: z.optional(
    z.string().max(50, { message: "Must be 50 or fewer characters long" })
  ),
  providerHospitalAddress2: z.optional(
    z.string().max(50, { message: "Must be 50 or fewer characters long" })
  ),
  providerHospitalCity: z.optional(
    z.string().max(20, { message: "Must be 20 or fewer characters long" })
  ),
  providerHospitalState: z.optional(
    z.string().max(20, { message: "Must be 20 or fewer characters long" })
  ),
  providerHospitalZip: z.optional(
    z.string().max(10, { message: "Must be 10 or fewer characters long" })
  ),
  providerImageUrl: z.optional(
    z.string().max(225, { message: "Must be 225 or fewer characters long" })
  ),
  isPrimaryProvider: z.optional(z.boolean()),
});

export default function HealthProvidersAddPage() {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({ defaultValues, resolver: zodResolver(zodRequest) });
  const { data: userQueryData } = useUserQuery();
  const { mutate: careProvidersPostMutation } = useCareProvidersPostMutation();
  const navigate = useNavigate();

  const onSubmit = (values) => {
    const phoneFormat = !values.providerPhone
      ? null
      : `${values.dialCode}-${values.providerPhone}`;
    careProvidersPostMutation({
      patientUserId: userQueryData?.patientUserId,
      ...values,
      providerPhone: phoneFormat,
    });
    reset();
    if (query.get("fromURL")) {
      navigate(query.get("fromURL"));
    } else {
      navigate("/user/hr/health-providers");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexFormContainer headerTitle={"Add Health Care Provider"}>
            <Controller
              name="providerName"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="* Provider Name"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerName?.message}
                  hasError={!!errors.providerName}
                />
              )}
            />
          <Flex direction={{ base: "column", medium: "row" }}>
            <Controller
              name="providerSpecialty"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="* Specialty"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerSpecialty?.message}
                  hasError={!!errors.providerSpecialty}
                />
              )}
            />
            <Controller
              name="providerSubSpecialty"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Sub-Specialty"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerSubSpecialty?.message}
                  hasError={!!errors.providerSubSpecialty}
                />
              )}
            />
          </Flex>
          <Flex direction={{ base: "column", medium: "row" }}>
            <Controller
              name="providerEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Email"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerEmail?.message}
                  hasError={!!errors.providerEmail}
                />
              )}
            />
            <Controller
              name="providerPhone"
              control={control}
              render={({ field }) => (
                <PhoneNumberField
                  {...field}
                  label="Phone"
                  defaultDialCode="+1"
                  dialCodeName="dialCode"
                  dialCodeLabel="Dial Code"
                  errorMessage={errors.providerPhone?.message}
                  hasError={!!errors.providerPhone}
                  onChange={(e) => {
                    const dialCode = getValues("dialCode");
                    if (!dialCode) {
                      setValue("dialCode", "+1");
                    }
                    field.onChange(e.target.value);
                  }}
                  onDialCodeChange={(e) => {
                    setValue("dialCode", e.target.value);
                  }}
                />
              )}
            />
          </Flex>

          <Divider size="small" />

            <Controller
              name="providerHospitalAffiliation"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Hospital Affiliation"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerHospitalAffiliation?.message}
                  hasError={!!errors.providerHospitalAffiliation}
                />
              )}
            />
            <Controller
              name="providerHospitalName"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Facility/Hospital Name"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerHospitalName?.message}
                  hasError={!!errors.providerHospitalName}
                />
              )}
            />
          <Flex direction={{ base: "column", medium: "row" }}>
            <Controller
              name="providerHospitalAddress1"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Address line 1"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerHospitalAddress1?.message}
                  hasError={!!errors.providerHospitalAddress1}
                />
              )}
            />
            <Controller
              name="providerHospitalAddress2"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Address line 2"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerHospitalAddress2?.message}
                  hasError={!!errors.providerHospitalAddress2}
                />
              )}
            />
          </Flex>
          <Flex direction={{ base: "column", medium: "row" }}>
            <Controller
              name="providerHospitalCity"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="City"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerHospitalCity?.message}
                  hasError={!!errors.providerHospitalCity}
                />
              )}
            />
            <Controller
              name="providerHospitalState"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="State"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.providerHospitalState?.message}
                  hasError={!!errors.providerHospitalState}
                />
              )}
            />
          </Flex>
          <Controller
            name="providerHospitalZip"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Zip code"
                {...field}
                value={field.value || ""}
                errorMessage={errors.providerHospitalZip?.message}
                hasError={!!errors.providerHospitalZip}
              />
            )}
          />
          <Controller
            name="isPrimaryProvider"
            control={control}
            render={({ field }) => (
              <SwitchField
                label="Mark as primary provider?"
                labelPosition="end"
                {...field}
                errorMessage={errors.isPrimaryProvider?.message}
                hasError={!!errors.isPrimaryProvider}
              />
            )}
          />
          <Button variation="primary" children="Save" type="submit" />
        </FlexFormContainer>
      </form>
    </>
  );
}
